import React from 'react';
import PropTypes from 'prop-types';
import c2pCentralLoginLink from '../components/c2pCentralLink';

/* The light markup staff-authored copy is written in, and its renderer.
 *
 * Four things and no more: blank-line paragraphs, "- " bullets, [text](url)
 * links, **bold**. That is the set the source marketing pages use, and the set
 * the Resource Library page intros use.
 *
 * DELIBERATELY NOT A MARKDOWN LIBRARY. This renders copy one person typed into
 * an admin form onto another person's screen — and, on the marketing side, onto
 * an external firm's screen. So the parser can only ever emit <p>, <ul>,
 * <strong> and an <a> whose scheme has been checked; anything else comes out as
 * literal text rather than as markup. A general markdown renderer would have to
 * be trusted with raw HTML passthrough, image tags and autolinking, none of
 * which anyone asked for here.
 *
 * C2P CENTRAL LINKS are routed through that site's login page on the way out
 * (components/c2pCentralLink.js) - a course or lesson link that goes straight
 * there strands the reader on a login form with no way back to the lesson. The
 * author writes the plain URL; only the href changes. b2b/Dashboard.jsx's copy
 * of this renderer does the same, and richtextLinkParity.test.jsx holds the two
 * to it.
 *
 * WHERE THIS LIVES, AND THE ONE COPY THAT STAYS SEPARATE. Both intranet readers
 * (routes/MarketingResources.js and routes/ResourceLibrary.js) import it from
 * here. `b2b/Dashboard.jsx` keeps its own copy on purpose: b2b/ imports nothing
 * from the intranet, which is what lets the external portal be reasoned about
 * on its own. That duplication is a boundary, not an oversight — this module is
 * for the intranet side of it, so a third copy isn't needed.
 */

// Two colours, inlined rather than imported: this module is presentational and
// self-contained, and pulling in an admin-section palette for a link colour
// would make a leaf component depend on a section it has nothing to do with.
const LINK_COLOR = '#0069a6';   // brand tealBlue
const TEXT_COLOR = '#58585b';   // brand charcoal

// An href must match this to become a link. Anything else (javascript:, data:)
// degrades to its own label text.
export const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

export function renderInline(text, keyPrefix) {
  const out = [];
  const pattern = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*/g;
  let cursor = 0;
  let n = 0;
  let match = pattern.exec(text);
  while (match !== null) {
    if (match.index > cursor) out.push(text.slice(cursor, match.index));
    if (match[1] !== undefined) {
      out.push(SAFE_HREF.test(match[2]) ? (
        <a key={`${keyPrefix}-${n}`} href={c2pCentralLoginLink(match[2])} target="_blank" rel="noopener noreferrer"
          style={{ color: LINK_COLOR }}>{match[1]}</a>
      ) : match[1]);
    } else {
      out.push(<strong key={`${keyPrefix}-${n}`}>{match[3]}</strong>);
    }
    cursor = pattern.lastIndex;
    n += 1;
    match = pattern.exec(text);
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

export function RichText({ text, style }) {
  if (!text) return null;
  const blocks = [];
  let bullets = [];
  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={`ul-${blocks.length}`} style={{ margin: '0 0 0.5rem', paddingLeft: '1.15rem' }}>
        {bullets.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} style={{ marginBottom: '0.15rem' }}>{renderInline(line, `li-${blocks.length}-${i}`)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };
  text.split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flushBullets(); return; }
    if (line.startsWith('- ')) { bullets.push(line.slice(2)); return; }
    flushBullets();
    blocks.push(
      // eslint-disable-next-line react/no-array-index-key
      <p key={`p-${i}`} style={{ margin: '0 0 0.45rem' }}>{renderInline(line, `p-${i}`)}</p>,
    );
  });
  flushBullets();
  return <div style={{ color: TEXT_COLOR, fontSize: '0.85rem', ...style }}>{blocks}</div>;
}
RichText.propTypes = { text: PropTypes.string, style: PropTypes.object };
RichText.defaultProps = { text: null, style: undefined };

// Shown under every rich-text box on the admin pages. The renderer supports
// exactly these four things, so saying so beats letting someone discover that a
// heading or a table comes out as literal text.
export const RICH_TEXT_HINT = 'Blank line = new paragraph · "- " starts a bullet · '
  + '[text](https://…) makes a link · **bold**. Paste C2P Central course links as they '
  + 'appear in the address bar - readers are sent via its login page so they land on the lesson.';

export default RichText;
